import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { projectPermission } from '../../utils/permissions';
import { toast } from 'react-toastify';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  fetchBoard, createTask,
  updateTaskStatus, optimisticBoardUpdate
} from '../../store/slices/tasksSlice';
import { fetchProjectById, fetchProjectMembers } from '../../store/slices/projectsSlice';
import { fetchSprints, fetchActiveSprint } from '../../store/slices/sprintsSlice';
import Modal from '../../components/common/Modal';
import { BlockedIndicator } from '../../components/common/DependencyManager';
import RichTextEditor from '../../components/common/RichTextEditor';
import ParentSelector from '../../components/common/ParentSelector';
import TicketTypeSelector, { TicketTypeIcon, InlineTypeSelector, TICKET_TYPES } from '../../components/common/TicketTypeSelector';
import SearchableSelect from '../../components/common/SearchableSelect';
import { Avatar } from '../../components/common/Avatar';
import { useTaskTypes } from '../../hooks/useTaskTypes';
import api from '../../services/api';
import './ProjectBoard.css';

// Priority configuration
const PRIORITIES = {
  highest: { color: '#ff5630', label: 'Highest' },
  high: { color: '#ff7452', label: 'High' },
  medium: { color: '#ffab00', label: 'Medium' },
  low: { color: '#2684ff', label: 'Low' },
  lowest: { color: '#57d9a3', label: 'Lowest' },
};

// Map backend field keys to human-readable labels for mandatory-field errors
const COMPLETION_FIELD_LABELS = {
  assignee: 'Assignee',
  actual_hours: 'Actual Hours',
  estimated_hours_or_story_points: 'Estimated Hours or Story Points',
};

const formatMandatoryFieldsError = (error) => {
  if (error && typeof error === 'object' && error.error === 'mandatory_fields_missing') {
    const names = (error.fields || []).map(f => COMPLETION_FIELD_LABELS[f] || f).join(', ');
    return `Cannot close ticket. Please fill required fields: ${names}`;
  }
  return null;
};

const ProjectBoard = ({ projectId: propProjectId, embedded = false, onOpenTask }) => {
  const { projectId: urlProjectId } = useParams();
  const projectId = propProjectId || urlProjectId;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { board, boardLoading } = useSelector((state) => state.tasks);
  const { currentProject, members } = useSelector((state) => state.projects);
  const { activeSprint } = useSelector((state) => state.sprints);

  // Gate board fetch until sprint data is resolved for the current project.
  // Without this, the board useEffect fires immediately with stale activeSprint=null,
  // causing a split-second flash of an incorrect/empty board before the real data arrives.
  const [sprintSettled, setSprintSettled] = useState(false);
  const settledForProject = useRef(null);

  // Load task types from API
  const { taskTypes, getTypeByKey } = useTaskTypes(projectId);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [selectedEpic, setSelectedEpic] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [showEpicPanel, setShowEpicPanel] = useState(false);
  const [epicPanelItems, setEpicPanelItems] = useState([]);
  const [epicPanelLoading, setEpicPanelLoading] = useState(false);
  const [epicPanelError, setEpicPanelError] = useState('');

  // Quick create state
  const [quickCreateColumn, setQuickCreateColumn] = useState(null);
  const [quickCreateTitle, setQuickCreateTitle] = useState('');
  const [quickCreateType, setQuickCreateType] = useState('story');
  const quickCreateRef = useRef(null);

  // Full create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    task_type: 'task',
    priority: 'medium',
    assignee_id: '',
    parent_id: null,
  });
  const [creating, setCreating] = useState(false);

  // Permissions
  const isViewer = user?.role === 'viewer';
  const canCreateTask = projectPermission(user, members, {
    projectRoles: ['admin', 'manager', 'member'],
    systemFallbackRoles: ['admin', 'manager'],
  });

  const canFilterByAnyUser = projectPermission(user, members, {
    projectRoles: ['admin', 'manager', 'member'],
    systemFallbackRoles: ['admin', 'manager'],
  });

  const getTaskUrl = useCallback((task) => {
    const parentKey = task.parent?.task_key;
    if (parentKey) {
      return `/projects/${projectId}/board/${parentKey}/${task.task_key}`;
    }
    return `/projects/${projectId}/board/${task.task_key}`;
  }, [projectId]);

  const getEpicUrl = useCallback((epic) => {
    return `/projects/${projectId}/board/${epic.task_key}`;
  }, [projectId]);

  const openEpic = useCallback((epic, event, viewMode = 'modal') => {
    if (event && (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1)) {
      window.open(getEpicUrl(epic));
      return;
    }
    if (onOpenTask) {
      onOpenTask(epic.task_key);
    } else {
      navigate(getEpicUrl(epic), {
        state: { viewMode, returnTo: location.pathname }
      });
    }
  }, [getEpicUrl, navigate, location.pathname, onOpenTask]);

  const fetchEpicPanel = useCallback(async () => {
    if (!projectId) return;
    setEpicPanelLoading(true);
    setEpicPanelError('');
    try {
      const response = await api.get(`/hierarchy/projects/${projectId}/epics/panel`);
      setEpicPanelItems(response.data.items || []);
    } catch (error) {
      setEpicPanelError('Failed to load epics');
    } finally {
      setEpicPanelLoading(false);
    }
  }, [projectId]);

  // Load initial data. fetchSprints resolves activeSprint — mark settled only after it
  // completes so the board fetch never fires with stale sprint state.
  useEffect(() => {
    // Reset gate whenever projectId changes so stale board is never shown
    if (settledForProject.current !== projectId) {
      settledForProject.current = projectId;
      setSprintSettled(false);
    }
    dispatch(fetchProjectById(projectId));
    dispatch(fetchProjectMembers(projectId));
    dispatch(fetchSprints({ projectId })).then(() => {
      setSprintSettled(true);
    });
    dispatch(fetchActiveSprint(projectId));
  }, [dispatch, projectId]);

  useEffect(() => {
    fetchEpicPanel();
  }, [fetchEpicPanel]);

  // Load board data — only after sprint data has resolved for this project
  useEffect(() => {
    if (!sprintSettled) return;
    const sprintId = activeSprint?.id || null;
    const assigneeIds = selectedAssignees.length > 0 ? selectedAssignees : null;

    dispatch(fetchBoard({
      projectId,
      sprintId,
      backlogOnly: false,
      assigneeId: assigneeIds?.length === 1 ? assigneeIds[0] : null,
      taskType: selectedType || null,
    }));
  }, [dispatch, projectId, sprintSettled, activeSprint, selectedAssignees, selectedType]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      // If the click originated in an element that intentionally ignores modal/overlay-close
      const clickedIgnore = e.target && e.target.closest && e.target.closest('[data-ignore-modal-close]');

      if (showAssigneeDropdown && !e.target.closest('.assignee-filter') && !clickedIgnore) {
        setShowAssigneeDropdown(false);
      }
      if (quickCreateColumn !== null && quickCreateRef.current && !quickCreateRef.current.contains(e.target) && !clickedIgnore) {
        setQuickCreateColumn(null);
        setQuickCreateTitle('');
      }

    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAssigneeDropdown, quickCreateColumn]);

  // Epic list for filter/panel
  const epics = epicPanelItems;

  // Filter tasks locally for search and epic
  const filteredColumns = useMemo(() => {
    return board.columns.map(col => ({
      ...col,
      tasks: col.tasks.filter(task => {
        // CRITICAL: Exclude Epics from Sprint Board
        // Epics should only appear in the Epics panel, not on the Sprint Board
        if (task.task_type === 'epic') return false;
        
        // Search filter
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          const matchesKey = task.task_key?.toLowerCase().includes(query);
          const matchesTitle = task.title?.toLowerCase().includes(query);
          if (!matchesKey && !matchesTitle) return false;
        }
        // Epic filter
        if (selectedEpic) {
          const epicId = parseInt(selectedEpic);
          const hasEpicParent = task.parent?.id === epicId;
          if (!hasEpicParent) return false;
        }
        // Assignee filter (multi-select)
        if (selectedAssignees.length > 0) {
          if (!task.assignee || !selectedAssignees.includes(task.assignee.id)) return false;
        }
        return true;
      }),
    }));
  }, [board.columns, searchQuery, selectedEpic, selectedAssignees]);

  // Total task count
  const totalTasks = filteredColumns.reduce((sum, col) => sum + col.tasks.length, 0);

  // Drag and drop handler
  const handleDragEnd = useCallback(async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const taskId = parseInt(draggableId);
    const newStatus = destination.droppableId;
    const newPosition = destination.index;

    // Optimistic update
    const newColumns = board.columns.map(col => {
      if (col.status === source.droppableId) {
        const tasks = [...col.tasks];
        const [removed] = tasks.splice(source.index, 1);
        if (col.status === newStatus) {
          tasks.splice(destination.index, 0, { ...removed, status: newStatus });
        }
        return { ...col, tasks };
      }
      if (col.status === destination.droppableId && source.droppableId !== destination.droppableId) {
        const tasks = [...col.tasks];
        const task = board.columns.find(c => c.status === source.droppableId).tasks[source.index];
        tasks.splice(destination.index, 0, { ...task, status: newStatus });
        return { ...col, tasks };
      }
      return col;
    });

    dispatch(optimisticBoardUpdate(newColumns));

    try {
      await dispatch(updateTaskStatus({ taskId, status: newStatus, position: newPosition })).unwrap();
    } catch (error) {
      dispatch(fetchBoard({
        projectId,
        sprintId: activeSprint?.id || null,
        backlogOnly: false,
      }));
      const mandatoryMsg = formatMandatoryFieldsError(error);
      toast.error(mandatoryMsg || 'Failed to update task status');
    }
  }, [board.columns, dispatch, projectId, activeSprint]);

  // Quick create handler
  const handleQuickCreate = async (e) => {
    e.preventDefault();
    if (!quickCreateTitle.trim() || !activeSprint) return;

    setCreating(true);
    try {
      const firstColumn = board.columns[0];
      const taskData = {
        title: quickCreateTitle.trim(),
        task_type: quickCreateType,
        priority: 'medium'
      };

      if (quickCreateType !== 'epic') {
        taskData.sprint_id = activeSprint.id;
        taskData.status = firstColumn?.status || 'to_do';
      }

      await dispatch(createTask({ projectId, taskData })).unwrap();
      const typeLabel = quickCreateType.charAt(0).toUpperCase() + quickCreateType.slice(1);
      const columnLabel = firstColumn?.status ? ` in ${firstColumn.status}` : '';
      toast.success(`${typeLabel} created${columnLabel}`);
      setQuickCreateTitle('');
      setQuickCreateType('story');
      setQuickCreateColumn(null);

      dispatch(fetchBoard({
        projectId,
        sprintId: activeSprint.id,
        backlogOnly: false,
      }));
    } catch (error) {
      toast.error(error || 'Failed to create issue');
    } finally {
      setCreating(false);
    }
  };

  // Create issue handler
  const handleCreateIssue = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setCreating(true);
    try {
      const isEpicType = formData.task_type === 'epic';
      const taskData = {
        ...formData,
        assignee_id: formData.assignee_id ? parseInt(formData.assignee_id) : null,
        sprint_id: isEpicType ? null : (activeSprint?.id || null),
      };
      await dispatch(createTask({ projectId, taskData })).unwrap();
      const typeLabel = formData.task_type.charAt(0).toUpperCase() + formData.task_type.slice(1);
      const sprintLabel = activeSprint?.name ? ` in ${activeSprint.name}` : '';
      toast.success(`${typeLabel} created${sprintLabel}`);
      setShowCreateModal(false);
      setFormData({
        title: '',
        description: '',
        task_type: 'task',
        priority: 'medium',
        assignee_id: '',
        parent_id: null,
      });

      dispatch(fetchBoard({
        projectId,
        sprintId: activeSprint?.id || null,
        backlogOnly: false,
      }));
    } catch (error) {
      toast.error(error || 'Failed to create issue');
    } finally {
      setCreating(false);
    }
  };

  // Assignee filter handlers
  const toggleAssignee = (userId) => {
    setSelectedAssignees(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      }
      return [...prev, userId];
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedAssignees([]);
    setSelectedEpic('');
    setSelectedType('');
  };

  const hasActiveFilters = searchQuery || selectedAssignees.length > 0 || selectedEpic || selectedType;

  // Get epic color (use parent epic color or default purple)
  const getEpicColor = (task) => {
    if (task.parent?.task_type === 'epic') {
      return '#6554c0'; // Could be dynamic based on epic
    }
    return null;
  };

  if (!sprintSettled || boardLoading) {
    const SKELETON_COLS = [3, 4, 2, 3, 1];   // card counts per column
    return (
      <div className="board-skeleton-wrap">
        {/* Mimic the filter bar */}
        <div className="board-skeleton-toolbar">
          {[80, 100, 72, 90].map((w, i) => (
            <div key={i} className="sk sk-line" style={{ width: w }} />
          ))}
        </div>
        <div className="board-skeleton-columns">
          {SKELETON_COLS.map((cardCount, ci) => (
            <div key={ci} className="board-skeleton-col">
              {/* Column header */}
              <div className="board-skeleton-col-header">
                <div className="sk sk-line" style={{ width: 90 }} />
                <div className="sk sk-badge" />
              </div>
              {/* Cards */}
              {Array.from({ length: cardCount }).map((_, ri) => (
                <div key={ri} className="board-skeleton-card">
                  <div className="sk sk-badge" style={{ width: 48 }} />
                  <div className="sk sk-line" style={{ width: `${[88, 75, 92, 68, 80][ri % 5]}%` }} />
                  <div className="sk sk-line-sm" style={{ width: `${[55, 65, 48, 70, 60][ri % 5]}%` }} />
                  <div className="board-skeleton-card-foot">
                    <div className="sk sk-circle" style={{ width: 22, height: 22 }} />
                    <div className="sk sk-badge" style={{ width: 56, marginLeft: 'auto' }} />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`jira-board ${embedded ? 'embedded' : ''}`}>
      {/* Filter Bar - Jira Style */}
      <div className="board-toolbar">
        <div className="toolbar-left">
          {/* Search */}
          <div className="search-box">
            <svg className="search-icon" viewBox="0 0 24 24" width="16" height="16">
              <path fill="currentColor" d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
            </svg>
            <input
              type="text"
              placeholder="Search issues..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => setSearchQuery('')}>×</button>
            )}
          </div>

          {/* Assignee Filter */}
          <div className="assignee-filter">
            <button className="assignee-button" onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}>
              <span className="assignee-label">Assignee</span>
              {selectedAssignees.length > 0 && (
                <span className="assignee-badge">{selectedAssignees.length}</span>
              )}
              <div className="assignee-avatars-inline">
                {selectedAssignees.slice(0, 4).map(userId => {
                  const member = members.find(m => m.user.id === userId);
                  if (!member) return null;
                  return (
                    <Avatar key={userId} user={member.user} size="sm" className="avatar-filter" />
                  );
                })}
                {selectedAssignees.length > 4 && (
                  <div className="avatar avatar-more avatar-sm">+{selectedAssignees.length - 4}</div>
                )}
              </div>
            </button>
            {showAssigneeDropdown && (
              <div className="assignee-dropdown">
                <div className="dropdown-header">Assignees</div>
                <div className="assignee-list">
                  <label className="assignee-option">
                    <input
                      type="checkbox"
                      checked={selectedAssignees.includes('unassigned')}
                      onChange={() => toggleAssignee('unassigned')}
                    />
                    <div className="avatar avatar-sm" style={{ background: '#f5f5f5', border: '1px dashed #ccc' }}>
                      ◯
                    </div>
                    <span>Unassigned</span>
                  </label>
                  {members.map(member => (
                    <label key={member.user.id} className="assignee-option">
                      <input
                        type="checkbox"
                        checked={selectedAssignees.includes(member.user.id)}
                        onChange={() => toggleAssignee(member.user.id)}
                      />
                      <Avatar user={member.user} size="sm" />
                      <span>{member.user.first_name} {member.user.last_name}</span>
                    </label>
                  ))}
                </div>
                {selectedAssignees.length > 0 && (
                  <button className="clear-selection" onClick={() => setSelectedAssignees([])}>
                    Clear selection
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Quick Filters Section */}
          <div className="quick-filters-section">
            {/* Epic Filter */}
            <div className="quick-filter-group epic-filter-group">
              <select
                className="quick-filter-dropdown"
                value={selectedEpic}
                onChange={(e) => setSelectedEpic(e.target.value)}
                title="Filter by epic"
              >
                <option value="">No epic</option>
                {epics.map(epic => (
                  <option key={epic.id} value={epic.id}>{epic.task_key} - {epic.title}</option>
                ))}
              </select>
              <button
                className={`epic-toggle-btn${showEpicPanel ? ' active' : ''}`}
                onClick={() => setShowEpicPanel(!showEpicPanel)}
                disabled={epics.length === 0 && !epicPanelLoading}
                title={epics.length === 0 ? 'No epics in this project' : (showEpicPanel ? 'Hide epic panel' : 'Show epic panel')}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M1.5 1.75V13.5h13.05a.75.75 0 010 1.5H.75a.75.75 0 01-.75-.75V1.75a.75.75 0 011.5 0zm14.28 2.53l-5.25 5.25a.75.75 0 01-1.06 0L7 7.06 4.28 9.78a.75.75 0 01-1.06-1.06l3.25-3.25a.75.75 0 011.06 0L10 8.06l4.72-4.72a.75.75 0 111.06 1.06z"/>
                </svg>
                Epics
              </button>
            </div>

            {/* Type Filter */}
            <div className="quick-filter-group">
              <select
                className="quick-filter-dropdown"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                title="Filter by type"
              >
                <option value="">Type</option>
                {Object.entries(taskTypes).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button className="clear-filters" onClick={clearFilters} title="Clear all filters">
              <svg viewBox="0 0 24 24" width="14" height="14">
                <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
              Clear
            </button>
          )}
        </div>

        <div className="toolbar-right">
          {/* Sprint Info */}
          {activeSprint && (
            <div className="sprint-info">
              <span className="sprint-name">{activeSprint.name}</span>
              {activeSprint.days_remaining != null && (
                <span className="sprint-days">{activeSprint.days_remaining}d remaining</span>
              )}
            </div>
          )}
          {!activeSprint && (
            <span className="no-sprint">No active sprint</span>
          )}
        </div>
      </div>

      {/* No active sprint banner */}
      {!activeSprint && !boardLoading && (
        <div className="board-no-sprint-banner">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <span>No active sprint. Tickets in planned sprints are visible in the <Link to={`/projects/${projectId}`}>Backlog</Link>.</span>
        </div>
      )}

      {/* Board Container */}
      <div className={`board-layout ${showEpicPanel ? 'panel-open' : ''}`}>
        <aside className={`epic-panel ${showEpicPanel ? 'open' : ''}`} aria-hidden={!showEpicPanel}>
          <div className="epic-panel-header">
            <span className="epic-panel-title">Epics</span>
            <button
              className="epic-panel-close"
              onClick={() => setShowEpicPanel(false)}
              title="Hide epic panel"
            >
              ×
            </button>
          </div>
          <div className="epic-panel-body">
            {epicPanelLoading && (
              <div className="epic-panel-state">Loading epics...</div>
            )}
            {!epicPanelLoading && epicPanelError && (
              <div className="epic-panel-state error">{epicPanelError}</div>
            )}
            {!epicPanelLoading && !epicPanelError && epics.length === 0 && (
              <div className="epic-panel-state">No epics yet</div>
            )}
            {!epicPanelLoading && !epicPanelError && epics.length > 0 && (
              <div className="epic-panel-list">
                {epics.map((epic) => {
                  const progress = epic.progress_percent || 0;
                  return (
                    <div key={epic.id} className="epic-panel-item">
                      <div className="epic-panel-item-header">
                        <button
                          className="epic-panel-link"
                          onClick={(e) => openEpic(epic, e)}
                          title={`${epic.task_key} - ${epic.title}`}
                        >
                          <span className="epic-key">{epic.task_key}</span>
                          <span className="epic-title">{epic.title}</span>
                        </button>
                        <button
                          className="epic-panel-edit"
                          onClick={(e) => openEpic(epic, e)}
                          title="Quick edit"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                          </svg>
                        </button>
                      </div>
                      <div className="epic-panel-meta">
                        <span className="epic-status">{epic.status}</span>
                        <span>{epic.story_count} stories</span>
                        <span>{epic.child_count} issues</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="epic-panel-progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={progress}>
                        <div className="epic-panel-progress-bar" style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </aside>

        <div className="board-scroll-container">
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="board-columns">
              {filteredColumns.map((column, columnIndex) => (
                <div key={column.status} className="board-column">
                {/* Column Header */}
                <div className="column-header" style={{ '--column-color': column.color || '#dfe1e6' }}>
                  <span className="column-name">{column.name}</span>
                  <span className="column-count">{column.tasks.length}</span>
                </div>

                {/* Quick Create - Only in first column */}
                {columnIndex === 0 && canCreateTask && activeSprint && (
                  <div className="quick-create-wrapper" ref={quickCreateRef}>
                    {quickCreateColumn === 0 ? (
                      <form onSubmit={handleQuickCreate} className="quick-create-form">
                        <InlineTypeSelector
                          value={quickCreateType}
                          onChange={setQuickCreateType}
                          projectId={projectId}
                          excludeSubtasks
                          excludeEpics
                        />
                        <input
                          type="text"
                          className="quick-create-input"
                          placeholder="What needs to be done?"
                          value={quickCreateTitle}
                          onChange={(e) => setQuickCreateTitle(e.target.value)}
                          autoFocus
                          disabled={creating}
                        />
                        <div className="quick-create-actions">
                          <button type="submit" className="btn btn-sm btn-primary" disabled={creating || !quickCreateTitle.trim()}>
                            {creating ? '...' : 'Create'}
                          </button>
                          <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setQuickCreateColumn(null); setQuickCreateTitle(''); setQuickCreateType('story'); }}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <button className="quick-create-btn" onClick={() => setQuickCreateColumn(0)}>
                        <svg viewBox="0 0 24 24" width="14" height="14">
                          <path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                        </svg>
                        Create
                      </button>
                    )}
                  </div>
                )}

                {/* Column Content */}
                <Droppable droppableId={column.status}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`column-content ${snapshot.isDraggingOver ? 'drag-over' : ''}`}
                    >
                      {column.tasks.map((task, index) => (
                        <Draggable key={task.id} draggableId={String(task.id)} index={index}>
                          {(provided, snapshot) => (
                            <Link
                              to={getTaskUrl(task)}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`issue-card ${snapshot.isDragging ? 'dragging' : ''} ${task.is_blocked ? 'blocked' : ''}`}
                              onClick={(e) => {
                                if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) {
                                  return;
                                }
                                e.preventDefault();
                                if (onOpenTask) {
                                  onOpenTask(task.task_key);
                                } else {
                                  navigate(getTaskUrl(task), {
                                    state: { viewMode: 'modal', returnTo: location.pathname }
                                  });
                                }
                              }}
                            >
                              {/* Epic Label or Add Epic Button — only for main types (story, task) */}
                              <div className="epic-section">
                                {['story', 'task'].includes(task.task_type) && (
                                  task.parent?.task_type === 'epic' ? (
                                    <button
                                      className="epic-label-link"
                                      style={{ '--epic-color': getEpicColor(task) || '#6554c0' }}
                                      onClick={(e) => {
                                        // Stop propagation so parent Link doesn't trigger
                                        e.stopPropagation();
                                        e.preventDefault();
                                        // Allow ctrl/cmd click to open in new tab
                                        if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) {
                                          window.open(`/projects/${projectId}/board/${task.parent.task_key}`);
                                        } else if (onOpenTask) {
                                          onOpenTask(task.parent.task_key);
                                        } else {
                                          navigate(`/projects/${projectId}/board/${task.parent.task_key}`, {
                                            state: { viewMode: 'modal', returnTo: location.pathname }
                                          });
                                        }
                                      }}
                                      title={`Go to Epic: ${task.parent.title}`}
                                    >
                                      {task.parent.title}
                                    </button>
                                  ) : (
                                    <button
                                      className="add-epic-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        if (onOpenTask) {
                                          onOpenTask(task.task_key);
                                        } else {
                                          navigate(getTaskUrl(task), {
                                            state: { viewMode: 'modal', returnTo: location.pathname }
                                          });
                                        }
                                      }}
                                      title="Add an Epic to this task"
                                    >
                                      + Epic
                                    </button>
                                  )
                                )}
                              </div>

                              {/* Card Title */}
                              <h4 className="card-title">{task.title}</h4>

                              {/* Card Footer */}
                              <div className="card-footer">
                                <div className="card-meta">
                                  {/* Type Icon */}
                                  <TicketTypeIcon type={task.task_type} size="xs" config={getTypeByKey(task.task_type)} />

                                  {/* Issue Key */}
                                  <span className="issue-key">{task.task_key}</span>

                                  {/* Blocked Indicator */}
                                  {task.is_blocked && (
                                    <span className="blocked-badge" title="Blocked">
                                      <svg viewBox="0 0 24 24" width="12" height="12">
                                        <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9A7.902 7.902 0 014 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1A7.902 7.902 0 0120 12c0 4.42-3.58 8-8 8z" />
                                      </svg>
                                    </span>
                                  )}

                                  {/* Children Count */}
                                  {task.child_count > 0 && (
                                    <span className="children-badge" title={`${task.child_count} subtasks`}>
                                      <svg viewBox="0 0 24 24" width="12" height="12">
                                        <path fill="currentColor" d="M4 14h4v-4H4v4zm0 5h4v-4H4v4zM4 9h4V5H4v4zm5 5h12v-4H9v4zm0 5h12v-4H9v4zM9 5v4h12V5H9z" />
                                      </svg>
                                      {task.open_child_count || 0}/{task.child_count}
                                    </span>
                                  )}

                                  {/* Story Points */}
                                  {task.story_points && (
                                    <span className="story-points">{task.story_points}</span>
                                  )}
                                </div>

                                <div className="card-right">
                                  {/* Priority */}
                                  <span
                                    className="priority-dot"
                                    style={{ backgroundColor: PRIORITIES[task.priority]?.color }}
                                    title={PRIORITIES[task.priority]?.label}
                                  />

                                  {/* Assignee Avatar */}
                                  {task.assignee ? (
                                    <Avatar user={task.assignee} size="xs" />
                                  ) : (
                                    <div className="avatar avatar-xs unassigned" title="Unassigned">
                                      <svg viewBox="0 0 24 24" width="12" height="12">
                                        <path fill="currentColor" d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Link>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
              ))}
            </div>
          </DragDropContext>
        </div>
      </div>

      {/* Create Issue Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Ticket"
        size="medium"
      >
        <form onSubmit={handleCreateIssue}>
          {/* Issue Type - Jira-style selector at top */}
          <div className="form-group issue-type-selector">
            <label className="form-label">Issue Type <span className="required">*</span></label>
            <TicketTypeSelector
              value={formData.task_type}
              onChange={(type) => setFormData(prev => ({ ...prev, task_type: type, parent_id: null }))}
              required={true}
              placeholder="Select issue type"
              projectId={propProjectId}
              excludeEpics
            />
          </div>

          {/* Title with inline type indicator */}
          <div className="form-group">
            <label className="form-label">Title <span className="required">*</span></label>
            <div className="title-input-row">
              {formData.task_type && (
                <TicketTypeIcon type={formData.task_type} size="md" config={getTypeByKey(formData.task_type)} />
              )}
              <input
                type="text"
                className="form-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="What needs to be done?"
                autoFocus
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <RichTextEditor
              value={formData.description}
              onChange={(value) => setFormData({ ...formData, description: value })}
              placeholder="Add more details..."
              minHeight={100}
              maxHeight={200}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Priority</label>
            <select
              className="form-select"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            >
              {Object.entries(PRIORITIES).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
          </div>

          {['task', 'story', 'subtask', 'bug'].includes(formData.task_type) && (
            <div className="form-group">
              <label className="form-label">Parent</label>
              <ParentSelector
                projectId={parseInt(projectId)}
                taskId={null}
                taskType={formData.task_type}
                currentParentId={formData.parent_id}
                onChange={(parentId) => setFormData({ ...formData, parent_id: parentId })}
              />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Assignee</label>
            <SearchableSelect
              options={members.map(m => ({
                value: m.user.id,
                label: `${m.user.first_name} ${m.user.last_name}`,
                email: m.user.email
              }))}
              value={formData.assignee_id}
              onChange={(assigneeId) => setFormData({ ...formData, assignee_id: assigneeId })}
              placeholder="Unassigned"
              searchPlaceholder="Search users..."
              getOptionDescription={(opt) => opt.email}
              clearable={true}
              preferBelow={true}
            />
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" style={{ marginTop: '0px' }} onClick={() => setShowCreateModal(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={creating}>
              {creating ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ProjectBoard;
